import type { FirmFile, MatterFile, MatterTemplate } from "@/lib/types";

export type SignatureRequestVariableSignerSlot = {
  index: number;
  key: string;
  kind: "contact" | "user";
  label: string;
};

export const getSignatureRequestVariableSignerSlot = (
  variable: string,
): SignatureRequestVariableSignerSlot | null => {
  const match = variable.match(/^(UserSigner|Signer)(\d*)(?:_|$)/);
  if (!match) return null;

  const [, prefix, rawIndex] = match;
  const index = Math.max(Number(rawIndex || "1") - 1, 0);
  const kind = prefix === "UserSigner" ? "user" : "contact";
  const label =
    kind === "user" ? `Firm signer ${index + 1}` : `Signer ${index + 1}`;

  return {
    index,
    key: `${prefix}${index + 1}`,
    kind,
    label,
  };
};

export const getSignatureRequestVariableSignerSlots = (
  variables?: string[] | null,
) => {
  const slots = new Map<string, SignatureRequestVariableSignerSlot>();

  variables?.forEach((variable) => {
    const slot = getSignatureRequestVariableSignerSlot(variable);
    if (slot) slots.set(slot.key, slot);
  });

  return [...slots.values()].sort(
    (left, right) =>
      left.kind.localeCompare(right.kind) || left.index - right.index,
  );
};

export const getUniqueSignerCount = (
  variables?: string[] | null,
  prefix: string = "Signer",
) => {
  if (!variables?.length) {
    return 0;
  }

  const uniquePrefixes = new Set<string>();
  const regex = new RegExp(`^(${prefix}\\d*)(?=_)`);

  variables.forEach((variable) => {
    if (!variable.startsWith(prefix)) {
      return;
    }

    const match = variable.match(regex);
    if (match) {
      const base = match[1] === prefix ? `${prefix}1` : match[1];
      uniquePrefixes.add(base);
    } else if (variable === prefix) {
      uniquePrefixes.add(`${prefix}1`);
    }
  });

  return uniquePrefixes.size;
};

export const getRequiredSignerCountsFromVariables = (
  variables?: string[] | null,
) => ({
  requiredContactSignerCount: getUniqueSignerCount(variables, "Signer"),
  requiredUserSignerCount: getUniqueSignerCount(variables, "UserSigner"),
});

export const getRequiredSignerCounts = (
  fileOrTemplate: MatterFile | MatterTemplate | FirmFile | null,
) => {
  if (!fileOrTemplate) {
    return getRequiredSignerCountsFromVariables([]);
  }

  return getRequiredSignerCountsFromVariables(fileOrTemplate.variables);
};
